import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { BiX, BiDownload } from 'react-icons/bi';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Loading from '../common/Loading';
import OrderDetailModal from '../order/OrderDetailModal';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1500;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 16px;
  width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
  position: relative;
  animation: fadeIn 0.2s ease;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 768px) {
    width: 92vw;
    padding: 20px;
  }
`;

const CloseBtn = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  cursor: pointer;
`;

const Title = styled.h3`
  margin-bottom: 8px;
  font-size: 22px;
`;

const SubInfo = styled.div`
  color: #888;
  font-size: 15px;
  margin-bottom: 20px;
`;

const OrderTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td {
    padding: 12px 14px;
    text-align: center;
    border-bottom: 1px solid #eee;
    font-size: 15px;
  }
  thead tr {
    background: #2d3748;
    color: #fff;
  }
  tbody tr {
    cursor: pointer;
    &:hover {
      background: #fafafa;
    }
  }
`;

const Empty = styled.div`
  text-align: center;
  color: #999;
  padding: 60px 0;
  font-size: 16px;
`;

const ExcelButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #1d6f42;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  padding-right: 40px;
`;

const UserOrderModal = ({ user, onClose }) => {
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`/admin/users/orders/${user._id}`);
        setOrders(data);
      } catch (e) {
        console.error(e);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [user._id]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const totalAmount = orders
    ? orders.reduce((sum, o) => sum + (o.verified_price || 0), 0)
    : 0;

  const handleExcelDownload = () => {
    if (!orders || orders.length === 0) return;

    // 주문별 상품을 개별 행으로 펼침
    const rows = [];
    orders.forEach((order) => {
      if (order.items && order.items.length > 0) {
        order.items.forEach((item) => {
          rows.push({
            '발주번호': order.order_id,
            '발주일': order.time ? order.time.substring(0, 10) : '',
            '상품명': item.product_name,
            '수량': item.count,
            '단가': item.unit_price,
            '금액': item.count * item.unit_price,
            '주문총액': order.verified_price || '',
            '운송장': order.postNum || '',
          });
        });
      } else {
        rows.push({
          '발주번호': order.order_id,
          '발주일': order.time ? order.time.substring(0, 10) : '',
          '상품명': '',
          '수량': '',
          '단가': '',
          '금액': '',
          '주문총액': order.verified_price || '',
          '운송장': order.postNum || '',
        });
      }
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    // 컬럼 폭 설정
    ws['!cols'] = [
      { wch: 22 }, { wch: 12 }, { wch: 35 }, { wch: 6 },
      { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 30 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '주문내역');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/octet-stream' });
    saveAs(blob, `${user.company_name}_주문내역.xlsx`);
  };

  return (
    <>
      <Overlay onClick={handleOverlayClick}>
        <ModalBox>
          <CloseBtn onClick={onClose}><BiX size={28} /></CloseBtn>
          <HeaderRow>
            <div>
              <Title>{user.company_name} - 주문 내역</Title>
            </div>
            {orders && orders.length > 0 && (
              <ExcelButton onClick={handleExcelDownload}>
                <BiDownload size={16} /> 엑셀 다운로드
              </ExcelButton>
            )}
          </HeaderRow>
          <SubInfo>
            {user.manager_name} | {user.user_email}
            {orders && orders.length > 0 && (
              <> | 총 {orders.length}건 | 누적 {totalAmount.toLocaleString('ko-KR')}원</>
            )}
          </SubInfo>

          {loading ? (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Loading />
            </div>
          ) : !orders || orders.length === 0 ? (
            <Empty>주문 내역이 없습니다.</Empty>
          ) : (
            <OrderTable>
              <thead>
                <tr>
                  <th>발주번호</th>
                  <th>발주일</th>
                  <th>상품</th>
                  <th>금액</th>
                  <th>운송장</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order._id} onClick={() => setSelectedOrderId(order._id)}>
                    <td>{order.order_id}</td>
                    <td>{order.time ? order.time.substring(0, 10) : '-'}</td>
                    <td style={{ textAlign: 'left', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {order.items && order.items.length > 0
                        ? order.items.map((i) => i.product_name).join(', ')
                        : '-'}
                    </td>
                    <td>{order.verified_price ? order.verified_price.toLocaleString('ko-KR') + '원' : '-'}</td>
                    <td style={{ fontSize: 12 }}>{order.postNum}</td>
                  </tr>
                ))}
              </tbody>
            </OrderTable>
          )}
        </ModalBox>
      </Overlay>
      {selectedOrderId && (
        <OrderDetailModal
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
        />
      )}
    </>
  );
};

export default UserOrderModal;
